import { useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useScroll, useSpring } from 'framer-motion';
import { ArrowRightIcon, ArrowUpRightIcon, MessageCircleIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { MentionedProducts } from '../journal/MentionedProducts';
import { ProductImage } from '../../ui/ProductImage';
import { articlePath } from '../journal/journalRules';
import { useCatalog } from '../../contexts/CatalogContext';
import { founder, salon } from '../../data/content';
import { usePages } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { useLayerY } from '../../hooks/useParallax';
import { ease } from '../../core/format/motion';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';
import { btnPrimaryLg } from '../../ui/styles';
import { HOME_ARTICLES, JOURNAL_PRODUCTS } from './homeRules';

/** Hoe snel het zwevende beeld de muis volgt. */
const FOLLOW = {
  stiffness: 380,
  damping: 32,
  mass: 0.6
};

/**
 * "Vragen die ik in de salon het vaakst krijg", op klei: het enige blok dat Sarahs expertise
 * laat zien krijgt een eigen, donker vlak (shell-tekst, ≥ 5,6:1; nooit muted of kleine blush op klei).
 *
 * - Links het nieuwste artikel groot; daaronder "Uit dit artikel": de genoemde producten op een
 *   licht vlak, met een plusje. Zo verkoopt het journal ook.
 * - Rechts de andere vragen als rijen. Wijs je er met de muis een aan, dan zweeft het beeld mee
 *   (alleen computer met muis, niet bij "minder beweging").
 * - Onderaan de app-kaart in blush: het lichtste vlak, dus het slot van het blok.
 */
export function HomeJournal() {
  const pages = usePages();
  const {
    articles,
    productsByIds
  } = useCatalog();
  const reduce = useReducedMotion();
  const copy = pages.home.journal;
  const [lead, ...rest] = articles.slice(0, HOME_ARTICLES);
  const [hovered, setHovered] = useState<string | null>(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, FOLLOW);
  const y = useSpring(my, FOLLOW);
  // De grote foto beweegt binnen zijn kader licht mee.
  const leadRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: leadRef,
    offset: ['start end', 'end start']
  });
  const leadY = useLayerY(scrollYProgress, [-28, 28]);
  if (!lead) return null;
  const mentioned = productsByIds(lead.productIds ?? []).slice(0, JOURNAL_PRODUCTS);
  // Alleen een voorbeeld met een echt beeld; `previewImage` is dan altijd een niet-lege tekst.
  const preview = rest.find(a => a.slug === hovered && a.image);
  const previewImage = preview?.image || null;
  const onMove = (e: MouseEvent<HTMLUListElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    mx.set(e.clientX - box.left);
    my.set(e.clientY - box.top);
  };
  return <section className="bg-clay py-20 text-shell sm:py-24 lg:py-32" aria-labelledby="home-journal" data-id="element-7385" data-mp="1j74pq7dcaj64">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7386" data-mp="1n886qx74il8x">
        <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-7387" data-mp="1n9t2gq56ca8d">
          <div className="max-w-2xl" data-id="element-7388" data-mp="19a0dp0d7k431">
            <h2 id="home-journal" className={sectionHeading} data-id="element-7389" data-mp="19b1pj721sult">
              <HeadingText heading={copy.heading} accentClassName="text-blush" data-id="element-7390" data-mp="1e8l4682nr9ea" />
            </h2>
            {copy.body && <p className="mt-5 max-w-xl text-[17px] leading-relaxed" data-id="element-7391" data-mp="21xw6vv3kjdv2">{fill(copy.body)}</p>}
          </div>
          {copy.cta && <Link to="/journal" className={`${quietLink} text-shell`} data-id="element-7392" data-mp="1s7kr9baivg8c">
              {fill(copy.cta)}
              <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7393" data-mp="1x6nwavqi5mgb" />
            </Link>}
        </div>

        <div className="mt-14 grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-7394" data-mp="2kvynpmlyq4hs">
          <article className="lg:col-span-7" data-id="element-7395" data-mp="2pmypb5611aqm">
            <Link to={articlePath(lead)} className="group block" data-id="element-7396" data-mp="2pnqiyag2cye9">
              <div ref={leadRef} className="relative aspect-[4/3] overflow-hidden bg-ink/20" data-id="element-7397" data-mp="111njs4fla8ng">
                <motion.div style={leadY ? {
                y: leadY
              } : undefined} className="absolute inset-x-0 -inset-y-[5%]" data-id="element-7398" data-mp="wz0yynhc5nzr">
                  <MediaView media={{
                  image: lead.image || founder.portraitPalm,
                  alt: ''
                }} sizes="(min-width: 1024px) 55vw, 100vw" className="h-full w-full transition-transform duration-300 ease-soft group-hover:scale-[1.03]" data-id="element-7399" data-mp="rzo3jecef8lk" />
                </motion.div>
              </div>
              <p className="mt-6 text-[13px]" data-id="element-7400" data-mp="1fseata9cx3pi">
                {lead.category} · {lead.readTime}
              </p>
              <h3 className="mt-2 type-sub underline decoration-transparent underline-offset-[8px] transition-[text-decoration-color] duration-150 ease-soft group-hover:decoration-shell/60" data-id="element-7401" data-mp="1608nidu1vagn">
                {lead.title}
              </h3>
              <p className="mt-3 max-w-xl text-[17px] leading-relaxed" data-id="element-7402" data-mp="1ynuw60i4x5ad">{lead.excerpt}</p>
            </Link>

            {mentioned.length > 0 && <motion.div initial={reduce ? false : {
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '0px 0px -10% 0px'
          }} transition={{
            duration: 0.25,
            ease
          }} className="mt-8 bg-shell p-5 text-ink sm:p-6" data-id="element-7403" data-mp="262ey8icspgza">
                <h4 className="text-[13px] text-muted" data-id="element-7404" data-mp="2oxvfoxigcyev">Uit dit artikel</h4>
                <MentionedProducts products={mentioned} data-id="element-7405" data-mp="2txhql0at62ki" />
              </motion.div>}
          </article>

          {rest.length > 0 && <ul onMouseMove={reduce ? undefined : onMove} onMouseLeave={() => setHovered(null)} className="relative divide-y divide-shell/25 border-y border-shell/25 lg:col-span-5 lg:self-start" data-id="element-7406" data-mp="2yphwd9dk7pp0">
              {rest.map(a => <li key={a.slug} data-id="element-7407" data-mp="3cp0sf3t3jaky">
                  <Link to={articlePath(a)} onMouseEnter={() => setHovered(a.slug)} className="group grid grid-cols-[1fr_auto] gap-4 py-7" data-id="element-7408" data-mp="3cm4sj9xoubtv">
                    <span data-id="element-7409" data-mp="18lfrn7bjwgs8">
                      <span className="block text-[13px]" data-id="element-7410" data-mp="18m7ik4u7dqc6">
                        {a.category} · {a.readTime}
                      </span>
                      <span className="mt-2 block type-title transition-transform duration-200 ease-soft group-hover:translate-x-1" data-id="element-7411" data-mp="1hlcgpa1urhnt">
                        {a.title}
                      </span>
                      <span className="mt-2 block text-[15px] leading-relaxed" data-id="element-7412" data-mp="1rb4fyhkwcyym">{a.excerpt}</span>
                    </span>
                    <ArrowUpRightIcon className="mt-1 h-5 w-5 transition-transform duration-200 ease-soft group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7413" data-mp="25b6v3w1tlea6" />
                  </Link>
                </li>)}

              {!reduce && <AnimatePresence data-id="element-7414" data-mp="3sx03x44cm70o">
                  {preview && previewImage && <motion.div key={preview.slug} aria-hidden="true" style={{
              x,
              y
            }} initial={{
              opacity: 0,
              scale: 0.96,
              rotate: -2
            }} animate={{
              opacity: 1,
              scale: 1,
              rotate: -4
            }} exit={{
              opacity: 0,
              scale: 0.96
            }} transition={{
              duration: 0.18,
              ease
            }} className="pointer-events-none absolute left-0 top-0 z-raised hidden lg:block" data-id="element-7415" data-mp="3sz47tm28z60y">
                      <div className="-translate-x-[115%] -translate-y-1/2 bg-shell p-2 shadow-[0_18px_40px_-16px_rgba(43,36,32,0.45)]" data-id="element-7416" data-mp="308uj6hdaoi0u">
                        <ProductImage src={previewImage} alt="" fit="cover" width={176} className="h-[13rem] w-44" data-id="element-7417" data-mp="357fkyl8739re" />
                      </div>
                    </motion.div>}
                </AnimatePresence>}
            </ul>}
        </div>

        {/* De app-kaart: het lichtste vlak, dus het slot van het blok. */}
        {copy.askLine && <div className="mt-14 flex flex-col gap-5 bg-blush p-6 text-ink sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:p-8 lg:mt-16" data-id="element-7418" data-mp="271ooe9trzvoa">
            <div className="flex items-start gap-4" data-id="element-7419" data-mp="1y9360uu23qzz">
              <MessageCircleIcon className="mt-1.5 h-5 w-5 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7420" data-mp="1y8bkkcu6b9nf" />
              <div data-id="element-7421" data-mp="1t7nlr4zpdoin">
                <p className="type-title" data-id="element-7422" data-mp="1oanhmzmeyvu6">{fill(copy.askLine)}</p>
                {copy.askBody && <p className="mt-1 text-[15px] leading-relaxed text-muted" data-id="element-7423" data-mp="1od06zxui48h9">{fill(copy.askBody)}</p>}
              </div>
            </div>
            {copy.askCta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${btnPrimaryLg} shrink-0`} data-id="element-7424" data-mp="1fbjv1lo1fyax">
                {fill(copy.askCta)}
                <span className="sr-only" data-id="element-7425" data-mp="39m5nds3zckl7">{shopLabels.viaWhatsApp}</span>
              </a>}
          </div>}
      </div>
    </section>;
}